import { useState, useEffect } from "react";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import LineitemPreview from "../../campaign/LineitemPreview";

// Utility function for date formatting
const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const OrderDetailsModal = ({ creative_id, creative_name, onClose }) => {
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            setLoading(true);
            try {
                const result = await API({
                    url: "/alpha_cst_designer/order_details",
                    method: "POST",
                    data: { creative_id },
                });

                if (result.status === "SUCCESS" && Array.isArray(result.data) && result.data.length > 0) {
                    setOrderDetails(result.data[0]);
                } else {
                    Toast("error", result.message || "No order details found.");
                }
            } catch (error) {
                Toast("error", "Something went wrong while fetching order details.");
            }
            setLoading(false);
        };

        fetchOrderDetails();
    }, [creative_id]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative w-full max-w-3xl p-6 bg-white rounded shadow-lg">
                <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl" onClick={onClose}>
                    &times;
                </button>

                <div className="flex flex-col gap-2 items-center justify-center mb-4">
                    <h2 className="text-2xl font-bold">Order Details</h2>
                    <h6 className="px-2 py-1 shadow-md">{creative_name}</h6>
                </div>
                {loading ? (
                    <div className="flex justify-center items-center py-10">
                        <div className="w-6 h-6 border-4 border-blue-500 border-dotted rounded-full animate-spin"></div>
                    </div>
                ) : !orderDetails ? (
                    <p className="text-center text-red-600">No details available.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <section className="bg-slate-50 py-4 px-6 rounded-lg md:col-span-2">
                            <h6 className="mb-2">Order Details</h6>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Creative ID :</label>
                                <span>{creative_id}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Order ID :</label>
                                <span>{orderDetails.order_id || "N/A"}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Order Name :</label>
                                <span>{orderDetails.order_name || "N/A"}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Order Type :</label>
                                <span>{orderDetails.order_type || "N/A"}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Order Value :</label>
                                <span>₹{orderDetails.order_value || "0"}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Start Date :</label>
                                <span>{formatDate(orderDetails.o_start_date)}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">End Date :</label>
                                <span>{formatDate(orderDetails.o_end_date)}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Created Date :</label>
                                <span>{formatDate(orderDetails.created_date)}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Verified :</label>
                                <span>{orderDetails.verified === 1 ? "Yes" : "No"}</span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <label className="text-slate-500">Is Package :</label>
                                <span>{orderDetails.ispackage ? "Yes" : "No"}</span>
                            </div>
                        </section>

                        <section className="bg-slate-50 py-4 px-6 rounded-lg md:col-span-2">
                            <h6 className="mb-2">Payment Details</h6>
                            {orderDetails.payment_details ? (
                                <>
                                    <div className="flex justify-between mb-2">
                                        <label className="text-slate-500">Ref ID :</label>
                                        <span>{orderDetails.payment_details.ref_id || "N/A"}</span>
                                    </div>
                                    <div className="flex justify-between mb-2">
                                        <label className="text-slate-500">Received Amount :</label>
                                        <span>₹{orderDetails.payment_details.received_amount || "0"}</span>
                                    </div>
                                    <div className="flex justify-between mb-2">
                                        <label className="text-slate-500">Transaction Date :</label>
                                        <span>{formatDate(orderDetails.payment_details.transaction_date_time)}</span>
                                    </div>
                                    <div className="flex justify-between mb-2">
                                        <label className="text-slate-500">UPI ID :</label>
                                        <span>{JSON.parse(orderDetails.payment_details.mode_of_payment || "{}").upi_id || "N/A"}</span>
                                    </div>
                                </>
                            ) : (
                                <p className="text-slate-500">No payment details available.</p>
                            )}
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
};

const OrderDetails = ({ creative_id, creative_name, campaign_id }) => {
    const [showModal, setShowModal] = useState(false);
    const [showLineItem, setShowLineItem] = useState(false);

    return (
        <>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <button onClick={() => setShowModal(true)} className="text-blue-600 hover:underline whitespace-nowrap">
                    View order details
                </button>
                <button onClick={() => setShowLineItem(true)} className="text-blue-600 hover:underline whitespace-nowrap">
                    view line item details
                </button>
            </div>
            {showModal && <OrderDetailsModal creative_id={creative_id} creative_name={creative_name} onClose={() => setShowModal(false)} />}
            <SlDrawer
                open={!!(showLineItem && campaign_id)}
                onSlAfterHide={(e) => {
                    e.preventDefault();
                    setShowLineItem(false);
                }}
                style={{ "--size": "75vw" }}
            >
                {showLineItem && (
                    <LineitemPreview campaign_id={campaign_id} />
                )}
            </SlDrawer>
        </>
    );
};

export default OrderDetails;
